import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage({ user }) {
  const { userId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userId && allUsers.length > 0) {
      const user = allUsers.find(u => u.id === userId);
      if (user) {
        setSelectedUser(user);
        loadConversation(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, allUsers]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data.filter(u => u.id !== user.id));
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      const allMessages = response.data;

      const conversationMap = new Map();
      allMessages.forEach(msg => {
        const otherId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        if (!conversationMap.has(otherId) || new Date(msg.created_at) > new Date(conversationMap.get(otherId).created_at)) {
          conversationMap.set(otherId, msg);
        }
      });

      setConversations(Array.from(conversationMap.values()).sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      ));
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const loadConversation = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${otherUserId}`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const handleSelectUser = (otherUser) => {
    setSelectedUser(otherUser);
    loadConversation(otherUser.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedUser.id,
        content: newMessage
      });
      setNewMessage('');
      loadConversation(selectedUser.id);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="messages-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold text-[#2d3748]">
          Messages
        </h1>
        <Link to="/group-chats">
          <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="group-chats-btn">
            <Users className="w-4 h-4 mr-2" />
            Group Chats
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <Card className="border-gray-100 rounded-2xl shadow-sm lg:col-span-1">
          <CardContent className="p-0">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-[#2d3748]">Conversations</h2>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {conversations.map((conv) => {
                const otherUserId = conv.sender_id === user.id ? conv.receiver_id : conv.sender_id;
                const otherUser = allUsers.find(u => u.id === otherUserId);
                const otherUserName = otherUser?.display_name || otherUser?.full_name || conv.sender_name;

                return (
                  <div
                    key={conv.id}
                    onClick={() => otherUser && handleSelectUser(otherUser)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === otherUserId ? 'bg-[#5b7f95]/5' : ''
                    }`}
                    data-testid="conversation-item"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-[#5b7f95] text-white">
                          {otherUserName?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-[#2d3748] truncate">
                          {otherUserName || 'Unknown User'}
                        </p>
                        <p className="text-xs text-[#64748b] truncate">{conv.content}</p>
                        <p className="text-xs text-[#64748b] mt-1">
                          {formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {conversations.length === 0 && (
                <div className="p-8 text-center text-[#64748b] text-sm">
                  No conversations yet. Start one from the Directory!
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="border-gray-100 rounded-2xl shadow-sm lg:col-span-2">
          {selectedUser ? (
            <>
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={selectedUser.profile_image} />
                    <AvatarFallback className="bg-[#5b7f95] text-white">
                      {selectedUser.full_name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-[#2d3748]">{selectedUser.full_name}</p>
                    <p className="text-sm text-[#64748b]">{selectedUser.business_name}</p>
                  </div>
                </div>
              </div>

              <div className="h-[500px] overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${
                      msg.sender_id === user.id ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                        msg.sender_id === user.id
                          ? 'bg-[#5b7f95] text-white'
                          : 'bg-gray-100 text-[#2d3748]'
                      }`}
                      data-testid="message-bubble"
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          msg.sender_id === user.id ? 'text-white/70' : 'text-[#64748b]'
                        }`}
                      >
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}

                {messages.length === 0 && (
                  <div className="text-center py-12 text-[#64748b]">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-50 border-gray-200 rounded-full"
                    data-testid="message-input"
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="h-[600px] flex items-center justify-center text-[#64748b]">
              <p>Select a conversation to start messaging</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
